import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, X, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Live camera modal using getUserMedia().
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onCapture: (blob: Blob, dataUrl: string) => void
 */
export default function CameraCapture({ open, onClose, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  const stop = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setReady(false);
  }, []);

  const start = useCallback(async (mode) => {
    setError("");
    setReady(false);
    stop();
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("Camera is not supported on this device / browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 1920 },
          height: { ideal: 1440 },
        },
        audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        // Wait for metadata so first frame is real, not black
        await new Promise((resolve) => {
          const onLoaded = () => {
            v.removeEventListener("loadedmetadata", onLoaded);
            resolve();
          };
          v.addEventListener("loadedmetadata", onLoaded);
        });
        try {
          await v.play();
        } catch (err) {
          // Non-fatal — iOS may still autoplay via muted attribute
          if (process.env.NODE_ENV !== "production") console.debug("video.play noop", err);
        }
        setReady(true);
      }
    } catch (err) {
      if (err && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
        setError("Camera permission was denied. Please enable camera access in your browser settings.");
      } else if (err && err.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else if (err && err.name === "NotReadableError") {
        setError("Camera is in use by another app. Close it and retry.");
      } else {
        setError("Could not start the camera. " + (err?.message || ""));
      }
    }
  }, [stop]);

  // Start when opened, stop when closed
  useEffect(() => {
    if (open) start(facing);
    return stop;
  }, [open, facing, start, stop]);

  const flip = () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    start(next);
  };

  const capture = async () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !ready) return;
    setBusy(true);
    try {
      const w = v.videoWidth || 1280;
      const h = v.videoHeight || 960;
      c.width = w;
      c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(v, 0, 0, w, h);
      const dataUrl = c.toDataURL("image/jpeg", 0.9);
      const blob = await new Promise((res) => c.toBlob(res, "image/jpeg", 0.9));
      // Attach a filename so downstream compression sees the extension
      if (blob) {
        const file = new File([blob], `capture-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        await onCapture(file, dataUrl);
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex flex-col"
      data-testid="camera-capture-modal"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/70 text-white z-10">
        <span className="text-sm font-medium">Take a photo</span>
        <button
          type="button"
          onClick={onClose}
          className="size-9 rounded-full bg-white/15 hover:bg-white/25 grid place-items-center"
          data-testid="camera-close-btn"
          aria-label="Close camera"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* Video preview */}
      <div className="flex-1 relative overflow-hidden bg-black">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="w-full h-full object-contain bg-black"
          data-testid="camera-video"
        />
        <canvas ref={canvasRef} className="hidden" />

        {!ready && !error && (
          <div className="absolute inset-0 grid place-items-center text-white/80 text-sm">
            Starting camera…
          </div>
        )}

        {error && (
          <div className="absolute inset-0 grid place-items-center px-6">
            <div className="max-w-sm bg-white/10 border border-white/20 text-white text-sm rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="size-5 shrink-0 text-amber-300" />
              <div className="space-y-2">
                <p>{error}</p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => start(facing)}
                  data-testid="camera-retry-btn"
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className="flex items-center justify-around px-6 py-6 bg-black/80 text-white z-10">
        <button
          type="button"
          onClick={flip}
          disabled={!ready}
          className="size-11 rounded-full bg-white/15 hover:bg-white/25 grid place-items-center disabled:opacity-40"
          data-testid="camera-flip-btn"
          aria-label="Switch camera"
        >
          <RefreshCw className="size-5" />
        </button>

        <button
          type="button"
          onClick={capture}
          disabled={!ready || busy}
          className="relative size-16 rounded-full border-4 border-white grid place-items-center hover:scale-105 transition-transform disabled:opacity-40"
          data-testid="camera-capture-btn"
          aria-label="Capture photo"
        >
          <span className="size-12 rounded-full bg-white" />
          <Camera className="absolute size-5 text-black pointer-events-none" />
        </button>

        <div className="size-11" />
      </div>
    </div>
  );
}
